import { isSameDay } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { DateType } from '../DateRange.types';
import CustomHeader from './CustomHeader';
import './DatePicker.scss';
import { DATE_FORMATS } from '../DateRange.constants';

interface CustomDatePickerProps extends ReactDatePickerProps {
  inputValue: DateType;
  onChange: (date: DateType) => void;
  dataTestId?: string;
  selected?: Date | null | undefined;
  startDate?: Date | null | undefined;
  endDate?: Date | null | undefined;
  maxDate?: Date | null | undefined;
  minDate?: Date | null | undefined;
  selectsEnd?: boolean | undefined;
  selectsStart?: boolean | undefined;
}

const CustomDatePicker = ({
  inputValue,
  selected,
  startDate,
  endDate,
  onChange,
  maxDate,
  minDate,
  selectsEnd,
  selectsStart,
  dataTestId,
}: CustomDatePickerProps): React.ReactElement => {
  const pickerRef = useRef(null);
  const [keyValue, setKeyValue] = useState(0);

  useEffect(() => {
    try {
      // react-datepicker has a bug where when changing dates programmatically sometimes the state in the component
      // is not updated properly (CSNP-737). Therefore, in such cases we force react-datepicker to un-mount and mount again
      // by changing react key attribute
      // @ts-ignore
      const preselectedDateInState = pickerRef?.current?.state.preSelection;
      if (selected && !isSameDay(preselectedDateInState, selected)) {
        setKeyValue((value) => value + 1);
      }
    } catch (e) {
      // In case something breaks, do nothing and leave the date picker state as it is
    }
  }, [selected, minDate, maxDate, startDate, endDate]);

  return (
    <div className="react-datepicker" data-testid={dataTestId}>
      <DatePicker
        ref={pickerRef}
        key={keyValue}
        formatWeekDay={(weekDay) => weekDay.toString().slice(0, 1).toUpperCase()}
        renderCustomHeader={(props) => <CustomHeader {...props} inputValue={inputValue} />}
        inline
        disabledKeyboardNavigation
        selectsStart={selectsStart}
        selectsEnd={selectsEnd}
        selected={selected}
        startDate={startDate}
        endDate={endDate}
        maxDate={maxDate}
        minDate={minDate}
        dateFormat={DATE_FORMATS.MONTH_DAY_YEAR}
        onChange={onChange}
      />
    </div>
  );
};

export default CustomDatePicker;
